import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Camera, Upload, Plus, Minus, Check, MapPin, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FUEL_TYPES, SERVICE_TYPES } from '../../mock/mock';
import { toFuelList } from '../../lib/fuels';
import { api } from '../../lib/api';
import { getCachedStations, enqueueCapture, saveDraft } from '../../lib/offline';
import { photoErrorInfo } from '../../lib/uploadErrors';
import { compressPhoto, UPLOAD_TIMEOUT_MS } from '../../lib/photo';

// Día 1 del mes en horario Guatemala → "día de servicio completo"
export const isGtFullServiceDay = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' }).endsWith('-01');
import { PhotoZoomSheet } from '../../components/PhotoZoomSheet';
import { copyToClipboard } from '../../lib/clipboard';
import { toast } from 'sonner';

const MAX_PHOTO_AGE_MIN = 45;

const INITIAL_PRICES = { vpower: 38.00, super: 36.00, regular: 34.00, diesel: 32.50, ulsd: 33.50 };

const emptySlot = () => ({ photo: null, preview: null, ageMin: null, prices: { ...INITIAL_PRICES } });

const PriceEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const [station, setStation] = useState(null);
  const [fuels, setFuels] = useState(FUEL_TYPES);
  // One capture slot per service type: each has its own photo + prices
  const [slots, setSlots] = useState({ self: emptySlot(), full: emptySlot() });
  const [serviceType, setServiceType] = useState('self');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [coords, setCoords] = useState(null);
  const [distance, setDistance] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('idle');
  const [gpsFails, setGpsFails] = useState(0);
  const [noGps, setNoGps] = useState(false);
  const [photoView, setPhotoView] = useState(null);
  const [prefillSource, setPrefillSource] = useState(null);
  const [refPrices, setRefPrices] = useState(null);
  const [priceWarns, setPriceWarns] = useState(null);
  const [preFocus, setPreFocus] = useState({});
  const gpsRadius = Number(station?.gps_radius_m) || 100;

  const slot = slots[serviceType];
  const setSlot = (svc, patch) => setSlots(s => ({ ...s, [svc]: { ...s[svc], ...patch } }));

  useEffect(() => {
    Promise.all([
      api.get('/stations').catch(async () => ({ data: await getCachedStations() })),
      api.get('/prices', { params: { station_id: id, limit: 10 } }).catch(() => ({ data: [] })),
      api.get('/products/master').catch(() => ({ data: null })),
      api.get(`/stations/${id}/prefill-prices`).catch(() => ({ data: null })),
    ]).then(([sr, pr, mp, pfr]) => {
      const master = (mp.data && mp.data.length) ? toFuelList(mp.data) : FUEL_TYPES;
      const s = sr.data.find(x => x.id === id);
      setStation(s);
      if (s) {
        // Si la estación solo tiene UN servicio configurado, fijarlo de una vez
        const svcCfg = s.services || {};
        const en = SERVICE_TYPES.filter(t => svcCfg[t.id] !== false);
        if (en.length === 1) setServiceType(en[0].id);
        // Fuels: only the station's enabled products, with brand-specific display names from AD-P13
        const avail = Array.isArray(s.available_products) && s.available_products.length > 0 ? s.available_products : null;
        const base = master.filter(f => !avail || avail.includes(f.id));
        setFuels(base);
        api.get('/products/mappings', { params: { brand: s.brand } })
          .then(m => {
            const names = {};
            (m.data || []).forEach(x => { names[x.master_id] = x.name; });
            setFuels(base.map(f => ({ ...f, name: names[f.id] || f.name })));
          })
          .catch(() => {});
      }
      // Prefill: últimos precios AUTORIZADOS (oficial/capturas aprobadas) de la estación;
      // fallback: última captura propia del usuario. Si no hubo cambio, solo valida con la foto.
      const pf = pfr?.data;
      const hasPf = pf && ((pf.self && Object.keys(pf.self).length) || (pf.full && Object.keys(pf.full).length));
      if (hasPf) setPrefillSource(pf.source);
      if (pf) setRefPrices({ last: { self: pf.self || {}, full: pf.full || {} }, own: pf.own_ref || null, ownName: pf.own_ref_name || null });
      const latestOverall = pr.data[0]?.prices;
      setSlots(prev => {
        const next = { ...prev };
        ['self', 'full'].forEach(svc => {
          const authorized = hasPf && pf[svc] && Object.keys(pf[svc]).length ? pf[svc] : null;
          const match = authorized || pr.data.find(e => e.service_type === svc)?.prices || latestOverall;
          if (match) next[svc] = { ...next[svc], prices: { ...next[svc].prices, ...match } };
        });
        return next;
      });
    });
  }, [id]);

  const adjust = (k, d) => setSlot(serviceType, {
    prices: { ...slot.prices, [k]: Math.max(0, +((parseFloat(slot.prices[k]) || 0) + d).toFixed(2)) },
  });

  const haversine = (a, b) => {
    const R = 6371000, toRad = x => x * Math.PI / 180;
    const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  };

  const captureGPS = () => {
    setGpsStatus('locating');
    if (!navigator.geolocation || !station) {
      if (station) {
        const c = [station.lat + 0.0003, station.lng + 0.0002];
        const d = haversine(c, [station.lat, station.lng]);
        setCoords(c); setDistance(d); setGpsStatus(d <= gpsRadius ? 'ok' : 'too_far');
      } else { setGpsStatus('error'); }
      return;
    }
    const onOk = (p) => {
      const c = [p.coords.latitude, p.coords.longitude];
      const d = haversine(c, [station.lat, station.lng]);
      setCoords(c); setDistance(d); setNoGps(false);
      setGpsStatus(d <= gpsRadius ? 'ok' : 'too_far');
      if (d <= gpsRadius) toast.success('Ubicación validada');
      else toast(`Estás a ${d.toFixed(0)} m · será marcada`, { description: `Debe ser menos de ${gpsRadius} m` });
    };
    const onFail = (err) => {
      setGpsStatus('error');
      setGpsFails(n => n + 1);
      if (err?.code === 1) {
        toast.error('Permiso de ubicación denegado', {
          description: 'Activa la ubicación para esta app: Ajustes → Privacidad → Ubicación (Safari/Chrome) y pulsa Reintentar.',
          duration: 10000,
        });
      } else {
        toast.error('No se pudo obtener ubicación GPS', {
          description: 'Sal a cielo abierto o acércate a una ventana y pulsa Reintentar.',
          duration: 8000,
        });
      }
    };
    // 1er intento: alta precisión, acepta fix reciente (≤60 s), hasta 20 s
    navigator.geolocation.getCurrentPosition(onOk, (e1) => {
      if (e1?.code === 1) { onFail(e1); return; }
      // 2º intento automático: baja precisión (red/wifi, funciona bajo techo), acepta caché 5 min
      navigator.geolocation.getCurrentPosition(onOk, onFail, { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 });
    }, { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 });
  };

  const continueWithoutGPS = () => {
    setCoords([station.lat, station.lng]);
    setDistance(null);
    setNoGps(true);
    setGpsStatus('no_gps');
    toast('Continuarás sin GPS', { description: 'La captura quedará marcada como anomalía para revisión.', duration: 6000 });
  };

  const onFile = async (e, source) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const svc = serviceType;
    const ageMs = Date.now() - (f.lastModified || Date.now());
    const ageMin = Math.floor(ageMs / 60000);
    if (ageMin > MAX_PHOTO_AGE_MIN) {
      toast.error(`Foto demasiado antigua · ${ageMin} min`, {
        description: `Debe tener menos de ${MAX_PHOTO_AGE_MIN} min. Toma una nueva.`,
      });
      if (fileRef.current) fileRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
      return;
    }
    // Object URL (no base64 en estado): una foto original de 8-16MB como dataURL
    // re-renderizada en cada tecleo mataba la pestaña en teléfonos de poca RAM
    // ("se sale de la aplicación" — caso HONOR X5C Plus).
    const rawUrl = URL.createObjectURL(f);
    setSlot(svc, { photo: f, ageMin, preview: rawUrl });
    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';

    // Compress in background (keeps EXIF GPS/date); swap the slot when ready
    compressPhoto(f).then(cf => {
      if (cf && cf !== f) {
        setSlot(svc, { photo: cf, preview: URL.createObjectURL(cf) });
        URL.revokeObjectURL(rawUrl);
      }
    });

    // If uploaded from gallery, try to extract GPS from EXIF
    if (source === 'upload') {
      try {
        const fd = new FormData();
        fd.append('photo', f);
        const ins = await api.post('/photos/inspect', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const gps = ins.data?.gps;
        const exifAge = ins.data?.age_min;
        if (exifAge != null && exifAge > MAX_PHOTO_AGE_MIN) {
          toast.error(`Foto tiene ${Math.round(exifAge)} min según EXIF · debe ser < ${MAX_PHOTO_AGE_MIN}`);
          setSlot(svc, { photo: null, preview: null, ageMin: null });
          return;
        }
        if (gps && station) {
          const d = haversine([gps.lat, gps.lng], [station.lat, station.lng]);
          setCoords([gps.lat, gps.lng]);
          setDistance(d);
          setNoGps(false);
          setGpsStatus(d <= gpsRadius ? 'ok' : 'too_far');
          if (d <= gpsRadius) toast.success(`Ubicación extraída de la foto · ${d.toFixed(0)} m`);
          else toast(`Foto tomada a ${d.toFixed(0)} m · será marcada`, { description: `Más de ${gpsRadius} m` });
        } else {
          toast('La foto no tiene coordenadas GPS', { description: 'Usa "Validar" con tu ubicación actual' });
        }
      } catch {
        toast('No se pudo leer metadatos de la foto', { description: 'Usa "Validar" con tu ubicación actual' });
      }
    }
  };

  const svcCfg = station?.services || null;
  const isFullServiceDay = isGtFullServiceDay();
  const baseServices = (() => {
    if (!svcCfg) return SERVICE_TYPES;
    const en = SERVICE_TYPES.filter(t => svcCfg[t.id] !== false);
    return en.length > 0 ? en : SERVICE_TYPES;
  })();
  // Servicio Completo solo se captura el día 1 del mes (salvo estaciones solo-full)
  const fullHiddenToday = !isFullServiceDay && baseServices.some(t => t.id === 'full') && baseServices.some(t => t.id === 'self');
  const enabledServices = fullHiddenToday ? baseServices.filter(t => t.id !== 'full') : baseServices;
  const singleService = enabledServices.length === 1;

  const filledServices = enabledServices.filter(t => slots[t.id]?.photo);
  const gpsValidated = !!coords && (gpsStatus === 'ok' || gpsStatus === 'too_far' || gpsStatus === 'no_gps');
  const canSubmit = gpsValidated && filledServices.length > 0;

  const submit = async (skipWarn = false) => {
    if (filledServices.length === 0) {
      toast.error('Toma al menos una foto', { description: singleService ? `Esta estación solo maneja ${enabledServices[0].name}.` : 'Autoservicio y/o Servicio Completo — cada servicio lleva su propia foto.' });
      return;
    }
    if (!coords) { toast.error('No se obtuvo ubicación GPS. Vuelve a tomar/subir la foto o pulsa "Validar".'); return; }
    setSubmitting(true);

    // Only the station's enabled fuels, coerced to numbers
    const pricesFor = (svc) => Object.fromEntries(
      fuels.map(f => [f.id, +(parseFloat(slots[svc].prices[f.id]) || 0).toFixed(2)])
    );

    const buildOffline = (svc) => ({
      stationId: id, stationName: station.name, brand: station.brand,
      serviceType: svc, prices: pricesFor(svc), lat: coords[0], lng: coords[1], notes, noGps,
      photoBlob: slots[svc].photo, photoName: slots[svc].photo.name, photoType: slots[svc].photo.type,
      capturedAt: new Date().toISOString(),
    });

    // Rechazar precios fuera de rango (error de dedo / OCR)
    for (const t of filledServices) {
      const bad = Object.entries(pricesFor(t.id)).find(([, v]) => Number(v) !== 0 && (Number(v) < 10 || Number(v) > 100));
      if (bad) {
        toast.error(`${t.name}: precio fuera de rango en ${bad[0].toUpperCase()} (Q${Number(bad[1]).toFixed(2)})`, { description: 'Los precios deben estar entre Q10 y Q100. Usa 0 si no hay dato.', duration: 8000 });
        setSubmitting(false);
        return;
      }
    }

    // Confirmación anti-error: diferencia > Q1.00 vs el tracking anterior del
    // marker O vs el precio vigente de la estación propia → pedir verificación
    if (!skipWarn && refPrices) {
      const warns = [];
      for (const t of filledServices) {
        for (const [fid, v] of Object.entries(pricesFor(t.id))) {
          const val = Number(v);
          if (!val) continue;
          const last = Number(refPrices.last?.[t.id]?.[fid]) || 0;
          const own = Number(refPrices.own?.[t.id]?.[fid] ?? refPrices.own?.self?.[fid]) || 0;
          const fname = fuels.find(f => f.id === fid)?.name || fid.toUpperCase();
          if (last > 0 && Math.abs(val - last) > 1.0001) warns.push({ key: `${t.id}-${fid}-l`, svc: t.name, fuel: fname, val, ref: last, refLabel: 'su tracking anterior' });
          if (own > 0 && Math.abs(val - own) > 1.0001) warns.push({ key: `${t.id}-${fid}-o`, svc: t.name, fuel: fname, val, ref: own, refLabel: `el vigente de ${refPrices.ownName || 'tu estación'}` });
        }
      }
      if (warns.length > 0) {
        setPriceWarns(warns);
        setSubmitting(false);
        return;
      }
    }

    if (!navigator.onLine) {
      try {
        for (const t of filledServices) await enqueueCapture(buildOffline(t.id));
        toast.success(`Sin conexión · ${filledServices.length} captura(s) guardada(s) localmente`);
        navigate('/app/history');
      } catch { toast.error('No se pudo guardar localmente'); }
      finally { setSubmitting(false); }
      return;
    }

    let sent = 0;
    let flagged = 0;
    let queued = 0;
    // Día de servicio completo: enviar 'full' primero para que el backend
    // registre el servicio completo antes de validar el 'self'.
    const orderedServices = [...filledServices].sort((a, b) => (a.id === 'full' ? -1 : 1) - (b.id === 'full' ? -1 : 1));
    try {
      for (const t of orderedServices) {
        const svc = t.id;
        try {
          const fd = new FormData();
          fd.append('station_id', id);
          fd.append('service_type', svc);
          fd.append('prices', JSON.stringify(pricesFor(svc)));
          fd.append('lat', coords[0]);
          fd.append('lng', coords[1]);
          fd.append('notes', notes);
          fd.append('no_gps', noGps ? 'true' : 'false');
          fd.append('photo', slots[svc].photo);
          const r = await api.post('/prices', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: UPLOAD_TIMEOUT_MS,
            onUploadProgress: (ev) => { if (ev.total) setUploadPct(Math.round((ev.loaded / ev.total) * 100)); },
          });
          sent += 1;
          if (r.data.status === 'flagged' || r.data.gps_anomaly) flagged += 1;
        } catch (e) {
          if (!e.response) {
            await enqueueCapture(buildOffline(svc));
            sent += 1;
            queued += 1;
          } else {
            const det = e.response?.data?.detail;
            if (e.response.status === 409 && typeof det === 'string' && det.includes('SERVICIO COMPLETO')) {
              toast.error('Regla de Servicio Completo', { description: det, duration: 15000 });
            } else {
              const info = photoErrorInfo(e);
              toast.error(`${t.name}: ${info.title}`, { description: info.hint, duration: 8000 });
            }
          }
        } finally { setUploadPct(null); }
      }
      if (sent > 0) {
        if (queued > 0) toast(`${queued} captura(s) guardada(s) en tu teléfono`, { description: 'Conexión lenta o inestable · se enviarán automáticamente al recuperar señal.', duration: 8000 });
        else if (flagged > 0) toast(`${sent} captura(s) al tracking · ${flagged} con anomalía GPS reportada`);
        else toast.success(`${sent} captura(s) agregada(s) al tracking`);
        navigate('/app/history');
      }
    } finally { setSubmitting(false); setUploadPct(null); }
  };

  if (!station) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  if (station.is_own && !station.trackable_as_marker) {
    return (
      <div className="px-5 py-6" data-testid="own-no-tracking-block">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[13px] text-[#5a5a5a] mb-6" data-testid="own-no-tracking-back">
          <ArrowLeft size={15}/> Volver
        </button>
        <div className="bg-white border border-[#e8e5de] rounded-2xl p-6 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-[#e7eeff] flex items-center justify-center mx-auto">
            <Camera size={20} className="text-[#0f1d6f]"/>
          </div>
          <h2 className="font-serif text-[22px] leading-tight">Ya no se hace tracking de estaciones propias</h2>
          <p className="text-[13px] text-[#5a5a5a]">
            Los precios vigentes de <strong>{station.name}</strong> son los últimos autorizados.
            La foto del monolito solo se toma al confirmar un cambio de precio.
          </p>
          <Button
            onClick={() => navigate('/app/pushes')}
            className="w-full h-11 rounded-full bg-[#1c1c1c] text-[#f8f6f1]"
            data-testid="own-no-tracking-go-pushes"
          >
            Ir a Cambios de precio
          </Button>
        </div>
      </div>
    );
  }

  const gpsLabel = {
    idle: 'Pulsa "Validar" para verificar tu ubicación',
    locating: 'Obteniendo ubicación…',
    ok: `Ubicación validada · ${distance?.toFixed(0)} m`,
    too_far: `Estás a ${distance?.toFixed(0)} m · será marcada`,
    error: gpsFails >= 2 ? 'Sin GPS tras varios intentos' : 'No se pudo obtener GPS',
    no_gps: 'Sin GPS · se marcará para revisión',
  }[gpsStatus];
  const gpsBg = { idle: 'bg-[#f3f0e9]', locating: 'bg-[#dbeafe]', ok: 'bg-[#dcfce7]', too_far: 'bg-[#fef3c7]', error: 'bg-[#fee2e2]', no_gps: 'bg-[#fef3c7]' }[gpsStatus];

  const activeName = SERVICE_TYPES.find(t => t.id === serviceType)?.name || '';

  return (
    <div className="px-5 py-5 fade-in pb-32">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[12px] text-[#5a5a5a] mb-5"><ArrowLeft size={14}/> Volver</button>

      {isFullServiceDay && (
        <div className="mb-5 bg-[#dc2626] text-white rounded-xl p-4 text-center shadow-lg shadow-red-900/20" data-testid="full-service-day-banner">
          <p className="text-[15px] font-bold uppercase tracking-wide">Hoy es día de servicio completo</p>
          <p className="text-[12px] mt-1 opacity-95">Día 1 del mes: captura AMBOS servicios (Auto y Full) donde aplique. El 2º tracking del día NO se guardará sin completar 1 tracking de servicio completo.</p>
        </div>
      )}

      {fullHiddenToday && (
        <div className="mb-5 bg-[#fef3c7] border border-[#fcd34d] text-[#92400e] rounded-xl p-3 text-[12px]" data-testid="full-only-day1-note">
          <strong>Servicio Completo</strong> se captura únicamente el <strong>día 1 del mes</strong> · hoy solo Autoservicio.
        </div>
      )}

      <p className="text-[10px] tracking-wider uppercase text-[#8a8579]">{station.brand} · Captura</p>
      <div className="flex items-center gap-2 flex-wrap mt-1">
        <h1 className="font-serif text-[26px] leading-tight tracking-tight">{station.name}</h1>
        {station.bu && (
          <button
            onClick={async () => {
              const ok = await copyToClipboard(station.bu);
              if (ok) toast.success(`BU ${station.bu} copiado`, { description: 'Pégalo en el buscador de Estaciones (AD-P06) para editar esta estación.' });
            }}
            className="press font-mono text-[11.5px] px-2 py-0.5 rounded-full bg-[#e7eeff] text-[#0f1d6f] border border-[#c7d6ff]"
            title="Toca para copiar el BU"
            data-testid="capture-station-bu"
          >
            BU {station.bu}
          </button>
        )}
      </div>

      <div className={`mt-4 border rounded-xl p-3.5 flex items-center gap-3 ${gpsStatus === 'too_far' || gpsStatus === 'error' ? 'border-[#fde68a]' : 'border-[#e8e5de]'} ${gpsBg}`} data-testid="gps-status">
        <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center">
          {gpsStatus === 'locating' ? <Loader2 size={15} className="animate-spin"/> : <MapPin size={15}/>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-medium leading-tight">Paso 1 · Validar ubicación</p>
          <p className="text-[11px] text-[#5a5a5a] mt-0.5">{gpsLabel}</p>
        </div>
        {gpsStatus === 'ok' && <Check size={16} className="text-[#166534]"/>}
        {gpsStatus === 'too_far' && <AlertCircle size={16} className="text-[#92400e]"/>}
        {(gpsStatus === 'idle' || gpsStatus === 'error' || gpsStatus === 'too_far' || gpsStatus === 'no_gps') && (
          <Button onClick={captureGPS} disabled={gpsStatus === 'locating'} className="h-8 px-3 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[11.5px]" data-testid="btn-validate-gps">
            {gpsStatus === 'idle' ? 'Validar' : 'Reintentar'}
          </Button>
        )}
      </div>
      {gpsStatus === 'error' && gpsFails >= 2 && (
        <button
          onClick={continueWithoutGPS}
          className="press mt-2 w-full h-10 rounded-xl border border-[#fde68a] bg-[#fffbeb] text-[#92400e] text-[12.5px] font-medium"
          data-testid="btn-continue-no-gps"
        >
          Continuar sin GPS · la captura quedará marcada para revisión
        </button>
      )}

      {/* Service type selector — each service has its OWN photo + prices */}
      <div className={`mt-5 transition-opacity ${gpsValidated ? '' : 'opacity-50'}`} data-testid="service-section">
        <p className="text-[12px] tracking-wider uppercase text-[#8a8579] mb-2">Paso 2 · Tipo de servicio y fotos</p>
        {!gpsValidated && (
          <p className="text-[11px] text-[#92400e] bg-[#fef3c7] border border-[#fde68a] rounded-lg px-3 py-2 mb-2" data-testid="gps-first-hint">
            Primero valida tu ubicación (Paso 1) para habilitar las fotos.
          </p>
        )}
        <div className={`grid gap-2 ${singleService ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {enabledServices.map(t => {
            const done = !!slots[t.id]?.photo;
            const active = serviceType === t.id;
            return (
              <motion.button key={t.id} onClick={() => setServiceType(t.id)} whileTap={{ scale: 0.96 }}
                className={`relative h-11 rounded-full border text-[13px] transition-colors duration-150 ${active ? 'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]' : 'bg-white border-[#e8e5de] text-[#3a3a3a]'}`}
                data-testid={`service-toggle-${t.id}`}>
                {t.name}
                {done && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#15803d] flex items-center justify-center"
                    data-testid={`service-done-${t.id}`}>
                    <Check size={11} className="text-white"/>
                  </motion.span>
                )}
              </motion.button>
            );
          })}
        </div>
        <p className="text-[10.5px] text-[#8a8579] mt-1.5" data-testid="service-hint">
          {fullHiddenToday
            ? 'Hoy solo Autoservicio · el Servicio Completo de esta estación se captura el día 1 del mes.'
            : singleService
              ? `Esta estación solo maneja ${enabledServices[0].name} — un solo tracking.`
              : 'Cada servicio lleva su propia foto y sus precios. Completa uno o ambos.'}
        </p>
      </div>

      <motion.div
        key={serviceType}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >

      <div className="mt-4">
        <p className="text-[12px] tracking-wider uppercase text-[#8a8579] mb-2">Foto del tablero · {activeName}</p>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e, 'camera')} className="hidden" data-testid="camera-input"/>
        <input ref={fileRef} type="file" accept="image/*" onChange={(e) => onFile(e, 'upload')} className="hidden" data-testid="upload-input"/>
        {slot.preview ? (
          <div className="relative">
            <button type="button" onClick={() => setPhotoView({ src: slot.preview, title: `Tablero · ${activeName}` })} className="block w-full" data-testid="capture-preview-zoom">
              <img src={slot.preview} alt={`Tablero ${activeName}`} className="w-full h-48 object-cover rounded-xl"/>
              <span className="block text-[10.5px] text-[#8a8579] mt-1 text-center">Toca la foto para verla con zoom</span>
            </button>
            <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider">{activeName}</span>
            <div className="absolute bottom-2 right-2 flex gap-2">
              <button onClick={() => cameraRef.current?.click()} className="bg-white/90 px-3 py-1.5 rounded-full text-[11px]" data-testid="btn-retake">Re-tomar</button>
              <button onClick={() => fileRef.current?.click()} className="bg-white/90 px-3 py-1.5 rounded-full text-[11px]" data-testid="btn-change-upload">Cambiar</button>
            </div>
            {slot.ageMin != null && (
              <p className="text-[10.5px] text-[#8a8579] mt-1">
                Foto de hace {slot.ageMin === 0 ? '< 1' : slot.ageMin} min
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => { if (!gpsValidated) { toast.error('Valida tu ubicación primero'); return; } cameraRef.current?.click(); }} className="press-soft h-36 bg-[#f3f0e9] border border-dashed border-[#d4cfc4] rounded-xl flex flex-col items-center justify-center gap-2 hover:bg-[#efece4] transition-colors" data-testid="btn-take-photo">
              <Camera size={22} className="text-[#8a8579]" strokeWidth={1.4}/>
              <span className="text-[12.5px] font-medium text-[#3a3a3a]">Tomar foto</span>
              <span className="text-[10.5px] text-[#8a8579] px-2 text-center">Usar cámara</span>
            </button>
            <button onClick={() => { if (!gpsValidated) { toast.error('Valida tu ubicación primero'); return; } fileRef.current?.click(); }} className="press-soft h-36 bg-white border border-dashed border-[#d4cfc4] rounded-xl flex flex-col items-center justify-center gap-2 hover:bg-[#f8f6f1] transition-colors" data-testid="btn-upload-photo">
              <Upload size={22} className="text-[#8a8579]" strokeWidth={1.4}/>
              <span className="text-[12.5px] font-medium text-[#3a3a3a]">Subir foto</span>
              <span className="text-[10.5px] text-[#8a8579] px-2 text-center">Galería · GPS del EXIF</span>
            </button>
          </div>
        )}
      </div>

      <div className="mt-5">
        <p className="text-[12px] tracking-wider uppercase text-[#8a8579] mb-2">Precios (Q) · {activeName}</p>
        {prefillSource && (
          <div className="mb-2 bg-[#dcfce7]/50 border border-[#bbf7d0] rounded-lg px-3 py-2 text-[11.5px] text-[#166534]" data-testid="prefill-banner">
            Precios precargados del último {prefillSource === 'official' ? 'precio oficial' : 'registro autorizado'} · si no hubo cambio, solo valida con la foto.
          </div>
        )}
        <div className="bg-white border border-[#e8e5de] rounded-xl divide-y divide-[#e8e5de]">
          {fuels.map(ft => {
            const lastRef = Number(refPrices?.last?.[serviceType]?.[ft.id]) || 0;
            const ownRef = Number(refPrices?.own?.[serviceType]?.[ft.id] ?? refPrices?.own?.self?.[ft.id]) || 0;
            const curV = parseFloat(slot.prices[ft.id]) || 0;
            const bigDiff = curV > 0 && ((lastRef > 0 && Math.abs(curV - lastRef) > 1.0001) || (ownRef > 0 && Math.abs(curV - ownRef) > 1.0001));
            return (
            <div key={ft.id}>
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <span className="w-1.5 h-6 rounded-full" style={{ background: ft.color }}/>
                  <span className="text-[13px] font-medium">{ft.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => adjust(ft.id, -0.10)} className="press w-8 h-8 rounded-full bg-[#f3f0e9] hover:bg-[#e8e5de] flex items-center justify-center"><Minus size={13}/></button>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={slot.prices[ft.id] ?? ''}
                    placeholder={preFocus[ft.id] != null ? String(preFocus[ft.id]) : ''}
                    onFocus={() => {
                      // Al tocar, el campo se VACÍA (el valor previo queda de placeholder)
                      // y se digita de cero. Evita el desync de teclados Android (Gboard)
                      // que congelaba el campo cuando React reescribía lo digitado.
                      const cur = slot.prices[ft.id];
                      if (cur !== '' && cur != null) {
                        setPreFocus(p => ({ ...p, [ft.id]: cur }));
                        setSlot(serviceType, { prices: { ...slot.prices, [ft.id]: '' } });
                      }
                    }}
                    onChange={(e) => {
                      let x = e.target.value.replace(',', '.').replace(/[^\d.]/g, '');
                      const i = x.indexOf('.');
                      if (i !== -1) x = x.slice(0, i + 1) + x.slice(i + 1).replace(/\./g, '');
                      const m = x.match(/^(\d*)(\.\d{0,2})?/);
                      const v = m ? (m[1] || '') + (m[2] || '') : '';
                      setSlot(serviceType, { prices: { ...slot.prices, [ft.id]: v } });
                    }}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      const prev = preFocus[ft.id];
                      setPreFocus(p => { const n = { ...p }; delete n[ft.id]; return n; });
                      if (raw === '' && prev != null) {
                        // No digitó nada: restaurar el precio anterior
                        setSlot(serviceType, { prices: { ...slot.prices, [ft.id]: prev } });
                        return;
                      }
                      const v = parseFloat(raw) || 0;
                      setSlot(serviceType, { prices: { ...slot.prices, [ft.id]: +Math.max(0, v).toFixed(2) } });
                    }}
                    className={`font-mono text-[15px] tabular-nums w-[84px] h-10 text-center rounded-lg focus:outline-none no-spin ${bigDiff ? 'bg-[#fef3c7] border-2 border-[#f59e0b] focus:border-[#b45309]' : 'bg-[#f8f6f1] border border-[#d4cfc4] focus:border-[#1c1c1c] focus:bg-white'}`}
                    data-testid={`price-input-${ft.id}`}
                  />
                  <button onClick={() => adjust(ft.id, 0.10)} aria-label={`Subir precio ${ft.name}`} className="press w-8 h-8 rounded-full bg-[#f3f0e9] hover:bg-[#e8e5de] flex items-center justify-center"><Plus size={13}/></button>
                </div>
              </div>
              {(lastRef > 0 || bigDiff) && (
                <div className={`px-4 pb-2 -mt-1.5 text-right text-[10px] ${bigDiff ? 'text-[#b45309] font-medium' : 'text-[#a8a397]'}`} data-testid={`price-ref-${ft.id}`}>
                  {lastRef > 0 ? `antes Q${lastRef.toFixed(2)}` : ''}
                  {bigDiff ? ' · difiere más de Q1 — verifica en el monolito' : ''}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
      </motion.div>


      <div className="mt-5">
        <p className="text-[12px] tracking-wider uppercase text-[#8a8579] mb-2">Notas (opcional)</p>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Promoción, descuento, observaciones…" className="bg-white border-[#e8e5de] focus:border-[#1c1c1c] text-[13px] resize-none" rows={3}/>
      </div>

      <Button onClick={() => submit()} disabled={submitting || !canSubmit} className="press-soft mt-6 w-full h-12 bg-[#0f1d6f] hover:bg-[#0a1450] text-[#f8f6f1] rounded-full text-[14px] disabled:opacity-40" data-testid="btn-submit-capture">
        {submitting
          ? <><Loader2 size={15} className="animate-spin mr-2"/> {uploadPct != null ? `Subiendo foto… ${uploadPct}%` : 'Enviando…'}</>
          : <><Check size={15} className="mr-2"/> Enviar {filledServices.length > 1 ? `${filledServices.length} capturas` : 'tracking'}</>}
      </Button>

      <Dialog open={!!priceWarns} onOpenChange={(o) => { if (!o) setPriceWarns(null); }}>
        <DialogContent className="max-w-[92vw] sm:max-w-md bg-[#f8f6f1]" data-testid="price-warn-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[20px] flex items-center gap-2">
              <AlertTriangle size={17} className="text-[#b45309]"/> Revisa los precios
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12.5px] text-[#5a5a5a]">Detectamos diferencias mayores a <strong>Q1.00</strong>. Verifica en el monolito antes de guardar:</p>
          <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
            {(priceWarns || []).map(w => {
              const d = +(w.val - w.ref).toFixed(2);
              return (
                <div key={w.key} className="bg-white border border-[#fcd34d] rounded-lg px-3 py-2 text-[12px]" data-testid={`price-warn-${w.key}`}>
                  <strong>{w.fuel}</strong> · {w.svc}: digitaste <strong className="font-mono">Q{w.val.toFixed(2)}</strong> vs {w.refLabel} <span className="font-mono">Q{w.ref.toFixed(2)}</span>{' '}
                  <span className={`font-mono font-bold ${d > 0 ? 'text-[#991b1b]' : 'text-[#166534]'}`}>({d > 0 ? '▲ +' : '▼ '}{d.toFixed(2)})</span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 mt-1">
            <Button variant="outline" onClick={() => setPriceWarns(null)} className="flex-1 h-11 rounded-full text-[12.5px]" data-testid="price-warn-fix">
              Corregir precios
            </Button>
            <Button onClick={() => { setPriceWarns(null); submit(true); }} className="flex-1 h-11 rounded-full bg-[#b45309] hover:bg-[#92400e] text-white text-[12.5px]" data-testid="price-warn-confirm">
              Sí, es correcto · Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Button
        onClick={async () => {
          if (filledServices.length === 0) { toast.error('Toma al menos una foto para guardar el borrador'); return; }
          if (!coords) { toast.error('Valida tu ubicación GPS antes de guardar el borrador'); return; }
          try {
            for (const t of filledServices) {
              await saveDraft({
                stationId: id, stationName: station.name, brand: station.brand, isOwn: !!station.is_own,
                serviceType: t.id,
                prices: Object.fromEntries(fuels.map(f => [f.id, +(parseFloat(slots[t.id].prices[f.id]) || 0).toFixed(2)])),
                lat: coords[0], lng: coords[1], noGps, notes,
                photoBlob: slots[t.id].photo, photoName: slots[t.id].photo.name, photoType: slots[t.id].photo.type,
                capturedAt: new Date().toISOString(),
              });
            }
            toast.success(`${filledServices.length} borrador(es) guardado(s)`, { description: 'Edita los precios y envía el tracking desde "Por enviar" — recuerda el ciclo de 1 hora.' });
            navigate('/app/drafts');
          } catch { toast.error('No se pudo guardar el borrador'); }
        }}
        disabled={submitting || !canSubmit}
        variant="outline"
        className="press-soft mt-2 w-full h-11 rounded-full border-[#0f1d6f]/40 text-[#0f1d6f] text-[13px] disabled:opacity-40"
        data-testid="btn-save-draft"
      >
        Guardar borrador · editar precios después
      </Button>
      {!canSubmit && !submitting && (
        <p className="text-[10.5px] text-[#8a8579] text-center mt-2" data-testid="submit-hint">
          {!gpsValidated ? 'Paso 1: valida tu ubicación GPS' : 'Paso 2: toma la foto de al menos un servicio'}
        </p>
      )}
      {canSubmit && filledServices.length > 0 && (
        <p className="text-[10.5px] text-[#8a8579] text-center mt-2" data-testid="capture-summary">
          Se enviará: {filledServices.map(t => t.name).join(' + ')}
        </p>
      )}
      <PhotoZoomSheet open={!!photoView} onOpenChange={(o) => { if (!o) setPhotoView(null); }} src={photoView?.src} title={photoView?.title}/>
    </div>
  );
};

export default PriceEntry;
